/**
 * Stickers of an ABCDior order: two labels with the EAN of the box and the
 * order id as barcodes, printed on 40 mm × 40 mm pages.
 */
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import JsBarcode from 'jsbarcode';
import spinner from '@/assets/images/spinner.svg';
import { fetchOrder, type Order } from '@/features/orders/api';
import { BARCODE_OPTIONS, boxEan, orderedSize } from './label-logic';

export function LabelPage() {
  const { custId: orderId } = useParams({ from: '/label/$custId' });
  const order = useQuery({
    queryKey: ['order', orderId] as const,
    queryFn: () => fetchOrder(orderId),
  });
  return order.data ? (
    <Stickers order={order.data} />
  ) : (
    <div className="label-container">
      <img src={spinner} alt="Loading" />
    </div>
  );
}

function Stickers({ order }: Readonly<{ order: Order }>) {
  const [errorMessage, setErrorMessage] = useState('');
  const [loaded, setLoaded] = useState(false);
  const box1 = useRef<SVGSVGElement>(null);
  const box2 = useRef<SVGSVGElement>(null);
  const bag1 = useRef<SVGSVGElement>(null);
  const bag2 = useRef<SVGSVGElement>(null);
  const size = orderedSize(order);
  const ean = boxEan(order, size);
  const client = (order.client ?? {}) as {
    lastName?: string;
    firstName?: string;
  };
  const store = (order.store ?? {}) as { name?: string };

  useEffect(() => {
    let message = '';
    if (ean) {
      try {
        for (const ref of [box1, box2]) {
          if (ref.current)
            JsBarcode(ref.current, ean, {
              ...BARCODE_OPTIONS,
              format: 'ean13',
            });
        }
      } catch (error) {
        console.error('Error on create bar code EAN13', error);
        message = String(error);
      }
    }
    if (order.displayId) {
      for (const ref of [bag1, bag2]) {
        if (ref.current)
          JsBarcode(ref.current, order.displayId, {
            ...BARCODE_OPTIONS,
            format: 'CODE128A',
          });
      }
    }
    setErrorMessage(message);
    setLoaded(true);
  }, [ean, order.displayId]);

  const description = (
    <>
      <div className="item description">{size?.m3SkuCentralFlow}</div>
      <div className="item description">{order.productName}</div>
      <div className="item description">
        CustomText: {order.customText} - {order.productColor}
      </div>
    </>
  );

  return (
    <div className="label-container" data-testid="label-page">
      {errorMessage && (
        <div className="error-message" data-testid="label-error">
          {errorMessage}
        </div>
      )}
      <div
        className={`sticker ${loaded ? '' : 'is-loading'}`}
        data-testid="label-sticker"
      >
        <div className="bullet" />
        <svg
          ref={box1}
          className={ean ? 'shown' : ''}
          aria-label="Box barcode"
        />
        {description}
        <svg ref={bag1} className="shown" aria-label="Order barcode" />
        <div className="item store">Store: {store.name}</div>
        <div className="item customer">OA n°: {order.oaCode}</div>
      </div>
      <div
        className={`sticker ${loaded ? '' : 'is-loading'}`}
        data-testid="label-sticker"
      >
        <svg
          ref={box2}
          className={ean ? 'shown' : ''}
          aria-label="Box barcode"
        />
        {description}
        <svg ref={bag2} className="shown" aria-label="Order barcode" />
        <div className="item store">Store: {store.name}</div>
        <div className="item customer">
          Customer Name: {client.lastName} {client.firstName}
        </div>
      </div>
    </div>
  );
}
